import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { apiGet, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerTechnician } from '@/types/scheduler';

interface TechnicianPage {
  items: SchedulerTechnician[];
  total: number;
  nextOffset: number | null;
}

export function useTechnicianSearch({
  search,
  zipCode,
  serviceTypeIds,
  enabled = true,
}: {
  search: string;
  zipCode?: string | null;
  serviceTypeIds: string[];
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const q = useDebounce(search.trim(), 300);
  const skills = [...new Set(serviceTypeIds)].sort().join(',');
  const prefix = [...schedulerKeys.all(), 'technician-search'];
  const query = useInfiniteQuery({
    queryKey: [...prefix, q, zipCode ?? '', skills],
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      apiGet<TechnicianPage>(operationsClient, schedulerEndpoints.technicianSearch(), {
        params: {
          q,
          zipCode: zipCode || undefined,
          serviceTypeIds: skills || undefined,
          offset: pageParam,
        },
        signal,
      }),
    getNextPageParam: (page) => page.nextOffset ?? undefined,
    enabled,
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  // Keep selected technicians' names available when a search hides their rows.
  const known = new Map<string, SchedulerTechnician>();
  for (const [, data] of queryClient.getQueriesData<{ pages: TechnicianPage[] }>({
    queryKey: prefix,
  })) {
    for (const page of data?.pages ?? []) {
      for (const technician of page.items) known.set(technician.id, technician);
    }
  }
  for (const technician of items) known.set(technician.id, technician);
  return {
    items,
    knownTechnicians: [...known.values()],
    total: query.data?.pages[0]?.total ?? 0,
    isLoading: query.isPending || q !== search.trim(),
    isError: query.isError,
    isFetchingMore: query.isFetchingNextPage,
    fetchMore: query.fetchNextPage,
    refetch: query.refetch,
  };
}
